import * as ExcelJS from "exceljs";
import { Combination, Constraints, UnmetDemands } from "../../types";
import { saveAs } from "file-saver";
import { autoWidth } from "../../utils";

const EXCEL_COLUMNS: Partial<ExcelJS.Column>[] = [
    { key: "prep", header: "Prep." },
    { key: "line", header: "Line" },
    { key: "market", header: "Market" },
    { key: "itemCode", header: "Item Code" },
    { key: "description", header: "Description" },
    { key: "variable", header: "Variable" },
    { key: "profit", header: "Profit" },
    { key: "demand", header: "Demand" },
    { key: "prepCapacity", header: "Prep. Capacity" },
    { key: "capacity", header: "Capacity" },
    { key: "prep_max_capacity", header: "Prep. Max. Capacity" },
    { key: "max_capacity", header: "Max. Capacity" },
    { key: "variableValue", header: "Variable Value" },
    { key: "optimalSolution", header: "Optimal Solution" },
];

const EXCEL_DEMANDS_COLUMNS: Partial<ExcelJS.Column>[] = [
    { key: "item", header: "Item" },
    { key: "market", header: "Market" },
    { key: "demand", header: "Demand" },
];

const EXCEL_UNMET_DEMANDS_COLUMNS: Partial<ExcelJS.Column>[] = [
    { key: "market", header: "Market" },
    { key: "item", header: "Item Code" },
    { key: "demand", header: "Demand" },
    { key: "unmet", header: "Unmet Demand" },
];

const EXCEL_RCCP_COLUMNS: Partial<ExcelJS.Column>[] = [
    { key: "line", header: "Line" },
    { key: "uiltization", header: "Uiltization" },
];

type ExportExcelParams = {
    combinations: Combination[];
    optimalValue: number;

    unmetDemands: UnmetDemands[];
    linesUtil: {
        [line: string]: number;
    };
    prepUtil: any;
    demands: {
        [key: string]: Constraints;
    };
};

export const exportExcel = async ({
    combinations,
    optimalValue,
    unmetDemands,
    linesUtil,
    prepUtil,
    demands,
}: ExportExcelParams) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Solution");

    // Add header
    worksheet.columns = EXCEL_COLUMNS;
    // Add data to the worksheet
    combinations.forEach((row) => {
        worksheet.addRow(row);
    });
    // Add optimal value under optimalSolution column
    worksheet.getRow(2).getCell("optimalSolution").value = optimalValue;

    //calculate and set the maximum width based on content for each column except the onces whose width are set
    autoWidth(worksheet, 10, 30);

    // add demands worksheet
    const demandsWorksheet = workbook.addWorksheet("Demands");
    // Add header
    demandsWorksheet.columns = EXCEL_DEMANDS_COLUMNS;
    // Add data to the worksheet
    Object.values(demands).forEach((demand) => {
        const row = { item: demand.item, market: demand.market, demand: demand.constraint };
        demandsWorksheet.addRow(row);
    });

    // add unmet demands worksheet
    const unmetDemandsWorksheet = workbook.addWorksheet("Unmet Demands");
    // Add header
    unmetDemandsWorksheet.columns = EXCEL_UNMET_DEMANDS_COLUMNS;
    // Add data to the worksheet
    unmetDemands.forEach((row) => {
        unmetDemandsWorksheet.addRow(row);
    });

    // add rccp worksheet
    const rccpWorksheet = workbook.addWorksheet("R.C.C.P. Utilization");
    // Add header
    rccpWorksheet.columns = EXCEL_RCCP_COLUMNS;
    // add "Line" label cell merged with two columnn under rccpWorksheet.columns
    rccpWorksheet.mergeCells("A2", "B2");
    // make center text with bold and light blue background
    rccpWorksheet.getCell("A2").alignment = { horizontal: "center" };
    rccpWorksheet.getCell("A2").font = { bold: true };
    rccpWorksheet.getCell("A2").fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFADD8E6" },
    };
    rccpWorksheet.getCell("A2").value = "Line";
    // Add data to the worksheet
    Object.keys(linesUtil).forEach((line) => {
        const uiltization = linesUtil[line] * 100 ?? 0;
        const row = { line: line, uiltization };
        rccpWorksheet.addRow(row);
    });

    // leave an empty row between rccp and prep utilization
    // then add prep lines label cell merged with two columnn under the last filled row
    const lastFilledRow = rccpWorksheet.actualRowCount;
    rccpWorksheet.mergeCells(`A${lastFilledRow + 2}`, `B${lastFilledRow + 2}`);
    // make center text with bold and light paige background
    rccpWorksheet.getCell(`A${lastFilledRow + 2}`).alignment = { horizontal: "center" };
    rccpWorksheet.getCell(`A${lastFilledRow + 2}`).font = { bold: true };
    rccpWorksheet.getCell(`A${lastFilledRow + 2}`).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD8BFD8" },
    };
    rccpWorksheet.getCell(`A${lastFilledRow + 2}`).value = "Prep Lines";
    // Add data to the worksheet
    Object.keys(prepUtil).forEach((prep) => {
        const uiltization = prepUtil[prep] * 100 ?? 0;
        const row = { line: prep, uiltization };
        rccpWorksheet.addRow(row);
    });

    const data = await workbook.xlsx.writeBuffer();
    const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8",
    });

    saveAs(blob, `allocator-result-${new Date().toISOString()}.xlsx`);
};
